<template>
  <IoRouterLink
      v-if="linkType !== 'button'"
      :link-url="buttonUrl"
      class="base-button"
      :class="classes"
      :link-target="buttonTarget"
      :style="style"
      :custom-handler="navigate"
  >
    <template v-if="icon">
      <span>
        <span>
          <slot></slot>
        </span>
        <span v-if="icon">
          <FaIcon :icon="icon"></FaIcon>
        </span>
      </span>
    </template>
    <template v-else>
      <span>
        <slot></slot>
      </span>
    </template>
  </IoRouterLink>
  <button
      v-else
      class="base-button"
      :class="classes"
      :style="style"
  >
    <template v-if="icon || loading">
      <span>
        <span>
          <slot></slot>
        </span>
        <span>
          <FaIcon v-if="icon" :icon="icon" />
          <FontAwesomeIcon v-else icon="fa-light fa-spinner" class="fa-pulse" />
        </span>
      </span>
    </template>
    <template v-else>
      <span>
        <slot></slot>
      </span>
    </template>
  </button>
</template>

<script lang="ts" setup>
import FaIcon from '@/components/FaIcon.vue';
import BranchInterface from '@/interfaces/BranchInterface';
import { scrollTo } from '@/utils/viewport';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSpinner } from '@fortawesome/pro-light-svg-icons';
import {
  computed, inject,
} from 'vue';
import { getBaseButtonCSSMargin } from '@/utils/BaseCSSHelper.js';

library.add(faSpinner);

const props = withDefaults(defineProps<{
  color?: string
  width?: string
  textAlign?: string
  align?: string
  linkType?: string
  pageUrl?: string
  mediaUrl?: string
  url?: string
  anchor?: string
  icon?: string
  branch?: BranchInterface
  loading?: boolean
}>(), {
  color: 'power',
  width: 'auto',
  textAlign: 'center',
  align: 'center',
  type: 'default',
  linkType: 'page',
  mediaId: '0',
});

const injectedAlignment = inject<string | undefined>('cardItemsAlign', undefined);
const alignment = injectedAlignment || props.textAlign;

const classes = computed(() => {
  const btnClasses = [];

  btnClasses.push(`btn-${props.color}`);
  btnClasses.push(`app-bg-${props.color} gradient`);

  if (props.width !== undefined) {
    btnClasses.push(props.width);
  }

  if (props.icon !== undefined) {
    btnClasses.push('btn--icon');
  }

  if (props.loading) {
    btnClasses.push('loading');
  }

  return btnClasses;
});

const style = computed(() => {
  const styles = {} as Record<string, string | undefined>;

  if (props.width !== 'full') {
    styles['--base-button-margin'] = getBaseButtonCSSMargin(props.align);
  }

  styles['--base-icon-grid-margin'] = getBaseButtonCSSMargin(alignment);

  return styles;
});

const isEmail = (email: string) => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email);

const buttonUrl = computed<string>(() => {
  let url;

  switch (props.linkType) {
    case 'page':
      url = props.pageUrl;
      break;
    case 'document':
      url = props.mediaUrl;
      break;
    case 'url':
      if (props.url && isEmail(props.url) && props.url.indexOf('mailto:') !== 0) {
        url = `mailto:${props.url}`;
      }
      url = props.url;
      break;
    default:
      url = undefined;
  }

  url = `${url || ''}`.trim();

  const anchor = `${props.anchor || ''}`.trim();

  if (
    props.linkType !== 'document'
      && anchor.length
  ) {
    url = `${url}#${anchor}`;
  }

  return url;
});

const buttonTarget = computed<string>(() => {
  switch (props.linkType) {
    case 'document':
    case 'url':
      if (props.url?.indexOf('javascript') === 0) {
        return '_self';
      }
      return '_blank';

    default:
      return '_self';
  }
});

const navigate = (next: () => void) => {
  if (props.linkType !== 'anchor') {
    next();
    return;
  }

  scrollTo(props.anchor || '', {
    windowPercentage: 0.2,
  });
};
</script>

<style lang="scss">
@import 'src/scss/component-helpers';

.base-button {
  $base-button-box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
  width: fit-content;
  display: block;
  border-radius: 65px !important;
  margin: var(--base-button-margin);
  box-shadow: $base-button-box-shadow;
  color: #FFFFFF;
  border: 0;
  @include font-regular(18px, 700);
  line-height: 1.25;
  padding: .9em 1.66667em !important;
  position: relative;
  overflow: hidden;
  min-height: 45px;
  text-decoration: none;

  @media(min-width: map-get($grid-breakpoints, 'lg')) {
    min-height: 55px;
  }

  &:hover {
    text-decoration: none;
  }

  @each $breakpoint, $size in $grid-breakpoints {
    @if ($breakpoint == 'xs') {
      &.full {
        width: 100%;
        text-align: v-bind(alignment);
      }
    } @else {
      &.full-#{$breakpoint} {
        @media (min-width: $size) {
          width: 100%;
          text-align: v-bind(alignment);
        }
      }
      &.auto-#{$breakpoint} {
        @media (min-width: $size) {
          width: fit-content;
        }
      }
    }
  }

  &:before {
    content: '';
    display: block;
    position: absolute;
    inset: 0;
    background-color: #293033;
    opacity: 0;
    transition: opacity .35s ease-in-out;
    z-index: 0;
  }

  > span {
    position: relative;
    z-index: 1;
  }

  &.btn--icon {
    > span {
      display: grid;
      grid-template-rows: auto;
      grid-template-columns: auto auto;
      align-items: center;
      width: fit-content;
      margin: var(--base-icon-grid-margin);

      > span {
        display: inline-block;

        &:first-child {
          grid-column: 1 / 2;
        }

        &:last-child {
          grid-column: 2 / 3;
          padding-left: 10px;
          font-size: .88889em;
          line-height: 1;
        }
      }
    }
  }

  &.btn-glas-fiber:before, &.btn-internet:before {
    background-color: #fff;
  }

  &:hover:not(.btn-white) {
    &:before {
      opacity: .15;
    }
  }

  &.btn-white {
    color: #FFFFFF;
    box-shadow: $base-button-box-shadow, inset 0 0 0 2px transparent;
    background: rgba(255, 255, 255, .4);
    transition: background .325s ease-in-out, color .325s ease-in-out, box-shadow .325s ease-in-out;

    &:hover {
      background: transparent;
      color: #fff;
      box-shadow: $base-button-box-shadow, inset 0 0 0 2px #ffffff;
    }
  }

  &.btn-dark-outline {
    color: $body-color;
    background: transparent;
    transition: background .325s ease-in-out, color .325s ease-in-out;
    box-shadow: $base-button-box-shadow, inset 0 0 0 2px $body-color;

    &:hover {
      background: $body-color;
      color: #FFFFFF;
    }
  }
}
</style>
